import { approximatePatternAttenuation } from './equipment.js';
import { freeSpacePathLossDb } from './index.js';

export interface InterferenceSector {
  id: string;
  name: string;
  lat: number;
  lon: number;
  frequencyMHz: number;
  channelWidthMHz: number;
  txPowerDbm: number;
  cableLossDb: number;
  antennaGainDbi: number;
  azimuthDeg: number;
  beamwidthDeg: number;
  frontToBackDb: number;
  maxRadiusM: number;
  ambientNoiseFloorDbm: number;
}

export interface InterferencePair {
  transmitterId: string;
  victimId: string;
  classification: 'co-channel' | 'adjacent-channel';
  channelOverlapFraction: number;
  distanceM: number;
  receivedInterferenceDbm: number;
}

export interface SectorInterferenceSummary {
  sectorId: string;
  ambientNoiseFloorDbm: number;
  predictedInterferenceDbm: number;
  effectiveNoiseFloorDbm: number;
  coChannelCount: number;
  adjacentChannelCount: number;
}

export interface NetworkInterferenceResult {
  pairs: InterferencePair[];
  sectors: SectorInterferenceSummary[];
}

export interface InterferenceMatrixInput {
  sectors: InterferenceSector[];
  targetFrequencyMHz: number;
  targetChannelWidthMHz: number;
  north: number;
  south: number;
  east: number;
  west: number;
  rows: number;
  cols: number;
  minimumInterferenceDbm?: number;
}

export interface InterferenceMatrixResult { values: Float32Array; minimumDbm: number; maximumDbm: number }

const EARTH_RADIUS_M = 6_371_008.8;
const NO_INTERFERENCE_DBM = -127;

function radians(degrees: number): number { return degrees * Math.PI / 180; }
function degrees(value: number): number { return value * 180 / Math.PI; }
function angularDifference(angleDeg: number, referenceDeg: number): number { return ((angleDeg - referenceDeg + 540) % 360) - 180; }

function path(origin: { lat: number; lon: number }, target: { lat: number; lon: number }): { distanceM: number; bearingDeg: number } {
  const latA = radians(origin.lat);
  const latB = radians(target.lat);
  const deltaLat = latB - latA;
  const deltaLon = radians(target.lon - origin.lon);
  const h = Math.sin(deltaLat / 2) ** 2 + Math.cos(latA) * Math.cos(latB) * Math.sin(deltaLon / 2) ** 2;
  const distanceM = 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
  const y = Math.sin(deltaLon) * Math.cos(latB);
  const x = Math.cos(latA) * Math.sin(latB) - Math.sin(latA) * Math.cos(latB) * Math.cos(deltaLon);
  return { distanceM, bearingDeg: (degrees(Math.atan2(y, x)) + 360) % 360 };
}

export function channelOverlapFraction(firstCenterMHz: number, firstWidthMHz: number, secondCenterMHz: number, secondWidthMHz: number): number {
  if (firstWidthMHz <= 0 || secondWidthMHz <= 0) throw new RangeError('Channel width must be positive');
  const overlapMHz = Math.max(0, Math.min(firstCenterMHz + firstWidthMHz / 2, secondCenterMHz + secondWidthMHz / 2) - Math.max(firstCenterMHz - firstWidthMHz / 2, secondCenterMHz - secondWidthMHz / 2));
  return overlapMHz / Math.min(firstWidthMHz, secondWidthMHz);
}

function combineDbm(values: number[]): number {
  const linear = values.filter(Number.isFinite).reduce((sum, value) => sum + 10 ** (value / 10), 0);
  return linear > 0 ? 10 * Math.log10(linear) : Number.NEGATIVE_INFINITY;
}

function receivedAtPoint(transmitter: InterferenceSector, target: { lat: number; lon: number }, targetFrequencyMHz: number, targetWidthMHz: number, receiveGainDbi = 0, receiveAzimuthDeg?: number, receiveBeamwidthDeg = 360, receiveFrontToBackDb = 0): { powerDbm: number; overlap: number; distanceM: number } | null {
  const overlap = channelOverlapFraction(transmitter.frequencyMHz, transmitter.channelWidthMHz, targetFrequencyMHz, targetWidthMHz);
  if (overlap <= 0) return null;
  const outbound = path(transmitter, target);
  if (outbound.distanceM <= 0 || outbound.distanceM > transmitter.maxRadiusM) return null;
  const transmitGainDbi = transmitter.antennaGainDbi + approximatePatternAttenuation(angularDifference(outbound.bearingDeg, transmitter.azimuthDeg), transmitter.beamwidthDeg, transmitter.frontToBackDb);
  let directionalReceiveGainDbi = receiveGainDbi;
  if (receiveAzimuthDeg !== undefined) {
    const inbound = path(target, transmitter);
    directionalReceiveGainDbi += approximatePatternAttenuation(angularDifference(inbound.bearingDeg, receiveAzimuthDeg), receiveBeamwidthDeg, receiveFrontToBackDb);
  }
  const powerDbm = transmitter.txPowerDbm + transmitGainDbi + directionalReceiveGainDbi - transmitter.cableLossDb - 2
    - freeSpacePathLossDb(transmitter.frequencyMHz / 1_000, outbound.distanceM / 1_000) + 10 * Math.log10(overlap);
  return { powerDbm, overlap, distanceM: outbound.distanceM };
}

export function calculatePlannedInterference(sectors: InterferenceSector[]): NetworkInterferenceResult {
  const pairs: InterferencePair[] = [];
  for (const victim of sectors) {
    for (const transmitter of sectors) {
      if (victim.id === transmitter.id) continue;
      const received = receivedAtPoint(transmitter, victim, victim.frequencyMHz, victim.channelWidthMHz, victim.antennaGainDbi, victim.azimuthDeg, victim.beamwidthDeg, victim.frontToBackDb);
      if (!received) continue;
      pairs.push({ transmitterId: transmitter.id, victimId: victim.id, classification: received.overlap >= 0.95 ? 'co-channel' : 'adjacent-channel', channelOverlapFraction: received.overlap, distanceM: received.distanceM, receivedInterferenceDbm: received.powerDbm });
    }
  }
  return {
    pairs,
    sectors: sectors.map((sector) => {
      const incoming = pairs.filter((pair) => pair.victimId === sector.id);
      const predictedInterferenceDbm = combineDbm(incoming.map((pair) => pair.receivedInterferenceDbm));
      return { sectorId: sector.id, ambientNoiseFloorDbm: sector.ambientNoiseFloorDbm, predictedInterferenceDbm, effectiveNoiseFloorDbm: combineDbm([sector.ambientNoiseFloorDbm, predictedInterferenceDbm]), coChannelCount: incoming.filter((pair) => pair.classification === 'co-channel').length, adjacentChannelCount: incoming.filter((pair) => pair.classification === 'adjacent-channel').length };
    }),
  };
}

export function calculateInterferenceMatrix(input: InterferenceMatrixInput): InterferenceMatrixResult {
  if (input.rows <= 0 || input.cols <= 0 || input.rows > 257 || input.cols > 257) throw new RangeError('Invalid interference matrix dimensions');
  const minimumInterferenceDbm = input.minimumInterferenceDbm ?? -120;
  const values = new Float32Array(input.rows * input.cols);
  values.fill(NO_INTERFERENCE_DBM);
  let minimumDbm = Number.POSITIVE_INFINITY;
  let maximumDbm = Number.NEGATIVE_INFINITY;
  for (let row = 0; row < input.rows; row += 1) {
    const lat = input.north + (input.south - input.north) * row / Math.max(1, input.rows - 1);
    for (let col = 0; col < input.cols; col += 1) {
      const lon = input.west + (input.east - input.west) * col / Math.max(1, input.cols - 1);
      const powers = input.sectors.flatMap((sector) => {
        const received = receivedAtPoint(sector, { lat, lon }, input.targetFrequencyMHz, input.targetChannelWidthMHz);
        return received ? [received.powerDbm] : [];
      });
      const combined = combineDbm(powers);
      if (combined < minimumInterferenceDbm) continue;
      const index = row * input.cols + col;
      values[index] = combined;
      minimumDbm = Math.min(minimumDbm, combined);
      maximumDbm = Math.max(maximumDbm, combined);
    }
  }
  return { values, minimumDbm: Number.isFinite(minimumDbm) ? minimumDbm : NO_INTERFERENCE_DBM, maximumDbm: Number.isFinite(maximumDbm) ? maximumDbm : NO_INTERFERENCE_DBM };
}
